/**
 * Export helpers for compliance documents (admin hub).
 *
 * - PDF  : browser print of the rendered document (Save as PDF).
 * - DOCX : full document, generated from the markdown source.
 * - Excel: the tabular sections only (risk register, inventory, KPI), one sheet
 *          per markdown table.
 */
import {
  Document,
  Packer,
  Paragraph,
  HeadingLevel,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
} from 'docx';
import * as XLSX from 'xlsx';

export interface ParsedTable {
  title: string;
  headers: string[];
  rows: string[][];
}

function cleanInline(s: string): string {
  return s
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
    .trim();
}

function isTableRow(line: string): boolean {
  return /^\s*\|.*\|\s*$/.test(line);
}

function isTableSeparator(line: string): boolean {
  return /^\s*\|?[\s:|-]+\|?\s*$/.test(line) && line.includes('-');
}

function splitRow(line: string): string[] {
  let l = line.trim();
  if (l.startsWith('|')) l = l.slice(1);
  if (l.endsWith('|')) l = l.slice(0, -1);
  return l.split('|').map((c) => cleanInline(c.trim()));
}

/** Extract all GFM tables from markdown, titled by the nearest preceding heading. */
export function parseMarkdownTables(md: string): ParsedTable[] {
  const lines = md.split('\n');
  const tables: ParsedTable[] = [];
  let lastHeading = 'Tabella';
  let inFence = false;
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim().startsWith('```')) {
      inFence = !inFence;
      i += 1;
      continue;
    }
    if (inFence) {
      i += 1;
      continue;
    }
    const heading = line.match(/^#{1,6}\s+(.*)$/);
    if (heading) {
      lastHeading = cleanInline(heading[1]!);
      i += 1;
      continue;
    }
    if (isTableRow(line) && i + 1 < lines.length && isTableSeparator(lines[i + 1]!)) {
      const headers = splitRow(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && isTableRow(lines[i]!)) {
        rows.push(splitRow(lines[i]!));
        i += 1;
      }
      tables.push({ title: lastHeading, headers, rows });
      continue;
    }
    i += 1;
  }
  return tables;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function sanitizeSheetName(name: string, index: number): string {
  const cleaned = name.replace(/[\\/?*[\]:]/g, ' ').trim().slice(0, 28);
  return cleaned.length > 0 ? `${index + 1}. ${cleaned}`.slice(0, 31) : `Tabella ${index + 1}`;
}

/** Excel: one sheet per markdown table. Throws if the document has no tables. */
export function exportTablesToExcel(slug: string, md: string): number {
  const tables = parseMarkdownTables(md);
  if (tables.length === 0) return 0;
  const wb = XLSX.utils.book_new();
  tables.forEach((t, idx) => {
    const aoa = [t.headers, ...t.rows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(t.title, idx));
  });
  XLSX.writeFile(wb, `${slug}.xlsx`);
  return tables.length;
}

const HEADING_LEVELS = [
  HeadingLevel.HEADING_1,
  HeadingLevel.HEADING_2,
  HeadingLevel.HEADING_3,
  HeadingLevel.HEADING_4,
  HeadingLevel.HEADING_5,
  HeadingLevel.HEADING_6,
];

function docxTable(headers: string[], rows: string[][]): Table {
  const headerRow = new TableRow({
    tableHeader: true,
    children: headers.map(
      (h) =>
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: h, bold: true })] })],
        }),
    ),
  });
  const bodyRows = rows.map(
    (r) =>
      new TableRow({
        children: headers.map(
          (_h, ci) =>
            new TableCell({ children: [new Paragraph(r[ci] ?? '')] }),
        ),
      }),
  );
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [headerRow, ...bodyRows],
  });
}

/** Parse markdown into docx blocks (headings, paragraphs, bullets, tables, quotes). */
function markdownToDocxChildren(md: string): (Paragraph | Table)[] {
  const lines = md.split('\n');
  const out: (Paragraph | Table)[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    const trimmed = line.trim();

    if (trimmed === '' || /^---+$/.test(trimmed)) {
      i += 1;
      continue;
    }

    if (trimmed.startsWith('```')) {
      const lang = trimmed.replace(/```/g, '').trim();
      const code: string[] = [];
      i += 1;
      while (i < lines.length && !lines[i]!.trim().startsWith('```')) {
        code.push(lines[i]!);
        i += 1;
      }
      i += 1; // closing fence
      if (lang === 'mermaid') {
        out.push(
          new Paragraph({
            children: [new TextRun({ text: '[Diagramma — visualizzabile nella versione web o PDF]', italics: true })],
          }),
        );
      } else {
        for (const c of code) {
          out.push(new Paragraph({ children: [new TextRun({ text: c, font: 'Courier New', size: 18 })] }));
        }
      }
      continue;
    }

    const heading = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      const level = heading[1]!.length - 1;
      out.push(
        new Paragraph({
          heading: HEADING_LEVELS[Math.min(level, 5)],
          children: [new TextRun({ text: cleanInline(heading[2]!) })],
        }),
      );
      i += 1;
      continue;
    }

    if (isTableRow(line) && i + 1 < lines.length && isTableSeparator(lines[i + 1]!)) {
      const headers = splitRow(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && isTableRow(lines[i]!)) {
        rows.push(splitRow(lines[i]!));
        i += 1;
      }
      out.push(docxTable(headers, rows));
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      out.push(
        new Paragraph({ bullet: { level: 0 }, children: [new TextRun(cleanInline(trimmed.replace(/^[-*]\s+/, '')))] }),
      );
      i += 1;
      continue;
    }

    if (trimmed.startsWith('>')) {
      out.push(
        new Paragraph({ children: [new TextRun({ text: cleanInline(trimmed.replace(/^>\s?/, '')), italics: true })] }),
      );
      i += 1;
      continue;
    }

    out.push(new Paragraph({ children: [new TextRun(cleanInline(trimmed))] }));
    i += 1;
  }
  return out;
}

/** DOCX: full document. */
export async function exportToDocx(slug: string, title: string, md: string): Promise<void> {
  const doc = new Document({
    sections: [
      {
        children: [
          new Paragraph({ heading: HeadingLevel.TITLE, children: [new TextRun(title)] }),
          ...markdownToDocxChildren(md),
        ],
      },
    ],
  });
  const blob = await Packer.toBlob(doc);
  triggerDownload(blob, `${slug}.docx`);
}

/**
 * PDF: print the element with id `compliance-print-area`. Toggling the body
 * class scopes the print stylesheet (see CompliancePrintStyles) to that node.
 */
export function printDocument(): void {
  document.body.classList.add('printing-compliance');
  const cleanup = () => {
    document.body.classList.remove('printing-compliance');
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  window.print();
  // Safety net if afterprint never fires (some browsers).
  setTimeout(cleanup, 60000);
}
